import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useCallback, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Notification } from 'rsuite';
import iconAlertYellow from '../../assets/icons/icon_alert_yellow_triangle.svg';
import iconCheckCircleGreen from '../../assets/icons/icon_check_circle_green.svg';
import { getPriceListRerunStatus, isPriceListRerunRunning } from '../../services/PriceListRerunService';
import {
  clearPendingPriceListRerun,
  dismissPriceListRerun,
  getPendingPriceListRerun,
  isPendingForCurrentUserToday,
  markPriceListRerunFinished,
  PendingPriceListRerun,
  PRICE_LIST_RERUN_STARTED_EVENT,
  PRICE_LIST_RERUN_STORAGE_KEY,
  PriceListRerunFinishedResult,
} from './pendingPriceListRerun';
import styles from './usePriceListRerunNotification.module.scss';

export const PRICE_LIST_RERUN_POLL_MS = 15000;

export const priceListRerunFinishedMessage = (r: PriceListRerunFinishedResult) => {
  if (r.status === 'CONCLUIDO_COM_FALHAS') return 'O reprocessamento da tabela de preços terminou, mas alguns itens não foram atualizados.';
  if (!r.written) return 'O reprocessamento da tabela de preços terminou. Nenhum preço precisou mudar.';
  return `O reprocessamento da tabela de preços terminou: ${r.written} ${r.written === 1 ? 'produto atualizado' : 'produtos atualizados'}.`;
};

const notificationKey = (executionId: string) => `price-list-rerun-${executionId}`;

/**
 * Aviso de fim do reprocessamento da tabela de preços, no mesmo formato do aviso de relatório pronto. O estado da
 * execução fica no localStorage: qualquer aba do usuário que disparou consulta o status enquanto ela roda, mostra o
 * aviso quando termina (também depois de recarregar) e fechar o aviso numa aba fecha nas outras.
 */
export const usePriceListRerunNotification = () => {
  const history = useHistory();
  const timer = useRef<number>();
  const checking = useRef(false);
  const mounted = useRef(false);
  const shownKey = useRef<string | null>(null);

  const closeShown = useCallback(() => {
    if (!shownKey.current) return;
    Notification.close(shownKey.current);
    shownKey.current = null;
  }, []);

  const notify = useCallback(
    (executionId: string, result: PriceListRerunFinishedResult) => {
      const key = notificationKey(executionId);
      if (shownKey.current === key) return;
      shownKey.current = key;
      const withFailures = result.status === 'CONCLUIDO_COM_FALHAS';
      Notification.open({
        key,
        className: styles['notification-price-list-rerun'],
        duration: 0,
        onClose: () => {
          shownKey.current = null;
          dismissPriceListRerun(executionId);
        },
        description: (
          <div className={styles['description-flex']}>
            <img src={withFailures ? iconAlertYellow : iconCheckCircleGreen} alt="" />
            <div>
              <p className={styles.text}>{priceListRerunFinishedMessage(result)}</p>
              <Button className={styles['link-flex']} appearance="link" onClick={() => history.push('/ipa/gerenciador-de-precos')}>
                <OpenInNewIcon className={styles['link-icon']} fontSize="inherit" />
                Ver no Gerenciador
              </Button>
            </div>
          </div>
        ),
      });
    },
    [history],
  );

  const pollStatus = useCallback(
    async (pending: PendingPriceListRerun) => {
      if (checking.current) return true;
      checking.current = true;
      try {
        const r = await getPriceListRerunStatus();
        // a resposta pode chegar depois de a tela desmontar ou de outra aba mudar a pendência (fechar o aviso,
        // gravar o fim, disparar outra execução): decide pelo que está gravado agora, não pelo de antes do await
        const current = getPendingPriceListRerun();
        if (!mounted.current || current?.executionId !== pending.executionId) return false;
        if (r.httpStatus === 200 && r.executionId === pending.executionId) {
          if (isPriceListRerunRunning(r.status)) return true;
          const result = { status: r.status, written: r.written };
          markPriceListRerunFinished(pending.executionId, result);
          if (!current.dismissed) notify(pending.executionId, result);
          return false;
        }
        if (r.httpStatus === 200 || r.httpStatus === 204 || r.httpStatus === 403) {
          // outra execução, nenhuma hoje ou outro cliente logado: a pendência não vale mais
          clearPendingPriceListRerun();
          return false;
        }
        return true;
      } catch {
        // falha de rede: tenta de novo no próximo ciclo
        return true;
      } finally {
        checking.current = false;
      }
    },
    [notify],
  );

  const sync = useCallback(async () => {
    window.clearTimeout(timer.current);
    const pending = getPendingPriceListRerun();
    if (!pending || !isPendingForCurrentUserToday(pending)) {
      closeShown();
      return;
    }
    if (pending.dismissed) {
      closeShown();
      return;
    }
    if (pending.finished) {
      notify(pending.executionId, pending.finished);
      return;
    }
    const keepPolling = await pollStatus(pending);
    // desmontou durante a consulta: o cleanup já rodou, não agenda outro ciclo
    if (keepPolling && mounted.current) timer.current = window.setTimeout(sync, PRICE_LIST_RERUN_POLL_MS);
  }, [closeShown, notify, pollStatus]);

  useEffect(() => {
    mounted.current = true;
    const onStorage = (event: StorageEvent) => {
      if (event.key === PRICE_LIST_RERUN_STORAGE_KEY || event.key === null) sync();
    };
    sync();
    window.addEventListener('storage', onStorage);
    window.addEventListener(PRICE_LIST_RERUN_STARTED_EVENT, sync);
    return () => {
      mounted.current = false;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PRICE_LIST_RERUN_STARTED_EVENT, sync);
      window.clearTimeout(timer.current);
    };
  }, [sync]);
};
